<script setup>
import { computed, nextTick, onMounted, reactive, ref, set, watch } from "vue";
import { useRouter } from "vue-router/composables";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import DatePicker from "vue2-datepicker";

import {
  ButtonSeverity,
  TuButton,
  TuInputLabel,
  TuInputSelect,
  TuInputSwitch,
  TuInputText,
} from "@tinyurldev/tu-ui-vue";

import { first, flattenDeep, isEmpty, union } from "lodash-es";

import ValidationMessage from "@Components/forms/ValidationMessage.vue";
import SingleFormResult from "@Components/tinyurl/shortener/single-form/Result.vue";
import UTMBuilder from "@Modals/UTMBuilder.vue";

import useLegacyStore from "@Composables/useLegacyStore";
import useShortenerForm, { ALIAS_BEHIND_MINIMUM, ALIAS_BRANDED_BEHIND_MINIMUM } from "@Composables/useShortenerForm";
import useLimit from "@Composables/useLimit";

import {
  buildAliasInput,
  buildDomainsInput,
  buildLongUrlInput,
  disabledExpirationDateRange,
  disabledExpirationTimeRange,
} from "./form-helper";

import LIMITS_TYPE from "@Constants/limits-type";
import { OVERAGE_STATUS } from "@Constants/overage-status";

import { getApiUrl } from "@Helpers/api/buildUrl";
import { TINYURL_EXPIRATION_DATE_PLACEHOLDER } from "@Helpers/constant";
import showModal from "@Helpers/functions/showModal";
import urlStringParamsToJson from "@Helpers/functions/urlStringParamsToJson";
import { sleep } from "@Helpers/support";

import SparkForm from "@Spark/resources/assets/js/forms/form";
import SparkHttp from "@Spark/resources/assets/js/forms/http";

const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);
const { getters } = useLegacyStore();
const linksLimit = useLimit(LIMITS_TYPE.LINKS_PER_MONTH);
const { allowedAvailableDomains, addDomain, defaultDomain } = useShortenerForm();
const router = useRouter();

const isMobileView = smallerOrEqual("xl");
const datepickerOpened = ref(false);
const createdTinyurl = ref({});
const isCustomExpirationVisible = ref(false);
const expirationDatePlaceholderText = ref(TINYURL_EXPIRATION_DATE_PLACEHOLDER);
const urlForm = reactive(
  new SparkForm({
    url: "",
    domain: "",
    alias: "",
    description: "",
    tags: [],

    // if null, alias will never expires
    // if has valid date, alias will use the date passed
    // if has an invalid date, alias will use the default date from their plan
    expires_at: null,
  }),
);
const isFormBusy = computed(() => urlForm.busy);
const currentTag = ref("");
const withUtm = ref(null);
const longUrlRef = ref(null);
const aliasHelperText = ref(ALIAS_BEHIND_MINIMUM);
const currentTagRef = ref(null);

const canManageExpiration = computed(() => getters.canManageExpiration);
const canUseAnalytics = computed(() => getters.canUseAnalytics);
const canUseTags = computed(() => getters.canUseTags);
const currentSubscription = computed(() => getters.getUserCurrentSubscription);
const isAccountUsageEnabled = computed(() => getters.isAccountUsageEnabled);
const isOnMobile = computed(() => getters.isOnMobile);
const isUserSubscribed = computed(() => getters.isUserSubscribed);
const tinyurls = computed(() => getters.tinyurls);
const urls = computed(() => getters.urls);

const expiresAt = computed({
  get() {
    return urlForm.expires_at ? moment.utc(urlForm.expires_at).toDate() : null;
  },
  set(value) {
    urlForm.expires_at = value ? moment(value).utc().toISOString() : null;
  },
});
const hasAdvancedOptions = computed(() => {
  return canManageExpiration.value;
});
const isFeatureLocked = computed(() => {
  if (!isAccountUsageEnabled.value) {
    return false;
  }

  if (
    !isUserSubscribed.value ||
    currentSubscription.value?.isLegacy
  ) {
    return linksLimit.isHardLimitReached.value;
  }

  return (
    linksLimit.isHardLimitReached.value &&
    !linksLimit.hasStatus(OVERAGE_STATUS.ALL_ENABLE)
  );
});
const tagHistory = computed(() => {
  const getTags = (aliases) => {
    return aliases.map((alias) => alias.tags).flat();
  };

  const tags = [
    ...tinyurls.value?.data.map((u) => getTags(u.aliases)),
    ...urls.value?.map((u) => getTags(u.aliases)),
  ];

  return union(flattenDeep(tags));
});

async function focusUrlInput() {
  await sleep(300);

  if (longUrlRef.value && longUrlRef.value.focus) {
    longUrlRef.value.focus();
  }
}

function setExpirationDate() {
  let date = null;

  if (isCustomExpirationVisible.value && expiresAt.value) {
    date = moment(expiresAt.value).utc().format("YYYY-MM-DDTHH:mm");
  }

  set(urlForm, "expires_at", date);
}

function manageTag(method, tag) {
  tag = tag.replaceAll(" ", "");

  const errors = { ...urlForm.errors.all() };
  delete errors.tags;
  urlForm.setErrors(errors);

  if (method == "add") {
    if (urlForm.tags.includes(tag)) {
      urlForm.errors.set({
        tags: ["The tag has already been taken."],
      });
    } else if (tag.length == 1) {
      urlForm.errors.set({
        tags: ["The tag must be at least 2 characters."],
      });
    } else if (tag.length > 45) {
      urlForm.errors.set({
        tags: ["The tag must not be greater than 45 characters."],
      });
    }

    if (!isEmpty(tag) && !urlForm.errors.has("tags")) {
      currentTag.value = "";
      set(urlForm, "tags", [...urlForm.tags, tag]);
    }
  }

  if (method == "remove") {
    urlForm.tags = urlForm.tags.filter((t) => t != tag);
  }
}
function addTag() {
  manageTag("add", currentTag.value);

  if (currentTagRef.value && currentTagRef.value.focus) {
    currentTagRef.value.focus();
  }
}
function removeTag(tag) {
  manageTag("remove", tag);
}
function setUnattendedTag() {
  const tag = currentTag.value;

  if (urlForm.tags.includes(tag)) {
    currentTag.value = null;
  } else {
    manageTag("add", tag);
  }
}

function addUtm() {
  if (!urlForm.url) {
    urlForm.setErrors({ url: ["The URL field is required."] });

    nextTick(() => {
      withUtm.value = false;
    });

    return;
  }

  showModal(UTMBuilder, {
    method: "add",
    destination: urlForm.url,
    callback: (rebuildUrl) => {
      urlForm.url = rebuildUrl;
    },
  });
}
function removeUtm() {
  const params = urlStringParamsToJson(urlForm.url);

  delete params["utm_source"];
  delete params["utm_medium"];
  delete params["utm_campaign"];
  delete params["utm_term"];
  delete params["utm_content"];

  const url = urlForm.url.split("?")[0];
  const str = new URLSearchParams(params).toString();

  urlForm.url = `${url}${str ? `?${str}` : ``}`;
}

function onDomainSelect(e) {
  const selectedDomain = allowedAvailableDomains.value.find((domain) => domain.domain === e);

  aliasHelperText.value = selectedDomain.isBranded
    ? ALIAS_BRANDED_BEHIND_MINIMUM
    : ALIAS_BEHIND_MINIMUM;
}
function createTinyurl() {
  setUnattendedTag();
  setExpirationDate();

  const requestUrl = getApiUrl("url/create");
  const requestPayload = urlForm;
  const requestHeaders = { DISABLE_ERROR_INTERCEPTORS: true };

  SparkHttp.post(requestUrl, requestPayload, requestHeaders)
    .then(({ data }) => {
      const tinyurl = first(data);

      tinyurl.aliases = data.map((urlData) => {
        return urlData.aliases.map((alias) => {
          alias.copied = false;
          return alias;
        });
      });

      createdTinyurl.value = tinyurl;
      Bus.$emit("tinyurl-created");

      if (canUseAnalytics.value) {
        router.push({
          name: "homepage",
          query: {
            active: true,
            alias: `${tinyurl.aliases[0][0].domain}/${tinyurl.aliases[0][0].alias}`,
          },
        });
      }
    })
    .catch(() => {
      if (urlForm.errors.has("url")) {
        focusUrlInput();
      }
    })
    .finally(() => {
      // track event in meta pixel
      if (window.fbq) {
        window.fbq("track", "shortern-url-button-clicked");
      }
    });
}
function shortenAnother() {
  isCustomExpirationVisible.value = false;
  withUtm.value = null;
  set(urlForm, "url", "");
  set(urlForm, "alias", "");
  set(urlForm, "description", "");
  set(urlForm, "tags", []);
  set(urlForm, "expires_at", null);
  createdTinyurl.value = {};
  focusUrlInput();
}

const longUrlProperties = computed(() => {
  const copyProperties = {
    help: null,
    errorMessage: urlForm.errors.get("form") || urlForm.errors.get("url"),
    isHtml: false,
  };
  const inputProperties = {
    isDisabled: isFeatureLocked.value,
  };

  return buildLongUrlInput({
    copyProperties,
    inputProperties,
  });
});
const domainsProperties = computed(() => {
  const copyProperties = {
    help: null,
    errorMessage: urlForm.errors.get("domain"),
    isHtml: false,
  };
  const domainOptions = allowedAvailableDomains.value?.map((domain) => {
    return {
      value: domain.domain,
      name: domain.domain,
    };
  });
  const inputProperties = {
    isDisabled: isFeatureLocked.value,
  };

  return buildDomainsInput({
    copyProperties,
    inputProperties,
    domainOptions,
  });
});
const aliasProperties = computed(() => {
  const copyProperties = {
    help: aliasHelperText.value,
    errorMessage: urlForm.errors.get("alias"),
    isHtml: false,
  };
  const inputProperties = {
    isDisabled: isFeatureLocked.value,
  };

  return buildAliasInput({
    copyProperties,
    inputProperties,
  });
});

watch(
  () => withUtm,
  (isOn) => {
    if (isOn.value) {
      addUtm();
    } else {
      removeUtm();
    }
  },
  { deep: true },
);

onMounted(() => {
  focusUrlInput();
  urlForm.domain = defaultDomain();
});
</script>

<template>
  <SingleFormResult
    v-if="!isEmpty(createdTinyurl)"
    :created-tinyurl="createdTinyurl"
    test-id-prefix="dashboard"
    @on-shorten-another="shortenAnother"
  />

  <form
    v-else
    autocomplete="off"
    class="single-form"
    :class="{ 'single-form--mobile': isMobileView }"
    @submit.prevent="createTinyurl"
  >
    <!-- Hidden input to prevent chrome autofill bug -->
    <input
      autocomplete="false"
      name="hidden-non-bulk"
      type="text"
      class="d-none"
    >

    <!-- Long URL Field -->
    <TuInputText
      v-model="urlForm.url"
      v-bind="longUrlProperties"
    />

    <!-- Domain and Alias Field -->
    <div class="single-form__domain-and-alias">
      <button
        class="single-form__domain-and-alias__hidden-button"
        type="submit"
        tabindex="-1"
        aria-hidden="true"
      />
      <TuInputSelect
        v-model="urlForm.domain"
        v-bind="domainsProperties"
        class="single-form__domain-and-alias__domain-input"
        @bottom-clicked="addDomain"
        @input="onDomainSelect"
      />
      <span
        v-show="!isMobileView"
        class="single-form__domain-and-alias__separator"
      >/</span>
      <TuInputText
        v-model="urlForm.alias"
        v-bind="aliasProperties"
        class="single-form__domain-and-alias__alias-input"
      />
    </div>

    <!-- Tags Field -->
    <div
      v-if="canUseTags"
      class="single-form__tags"
    >
      <TuInputLabel
        id="current-tag"
        class="single-form__tags__label"
        icon="fa-solid fa-tag"
        :label="`${$t('add_tags')} (Optional)`"
      />
      <div
        class="d-flex align-items-start flex-wrap"
        style="gap: 10px"
      >
        <button
          v-for="tag in urlForm.tags"
          :key="tag"
          type="button"
          data-test-id="dashboard_create_tinyurl_field_tag"
          class="btn single-form__tags__tag"
          @click="removeTag(tag)"
          @keydown.esc.stop.prevent="removeTag(tag)"
          @keydown.backspace.stop.prevent="removeTag(tag)"
        >
          <span data-test-id="dashboard_create_tinyurl_field_tag_text">
            {{ tag }}
          </span>
          <i
            data-test-id="dashboard_create_tinyurl_field_tag_btn"
            class="fa-solid fa-times ml-2"
          />
        </button>

        <div
          class="flex-center"
          style="max-width: 175px"
        >
          <input
            id="current-tag"
            ref="currentTagRef"
            v-model="currentTag"
            list="tag-history"
            name="current-tag"
            type="text"
            class="form-control single-form__tags__tag-input"
            :class="{ 'is-invalid': urlForm.errors.has('tags') }"
            :disabled="isFeatureLocked"
            placeholder="Enter Tag Name"
            @input="currentTag = $event.target.value.toLowerCase()"
            @keypress.prevent.enter="addTag"
          >
          <datalist id="tag-history">
            <option
              v-for="tag in tagHistory"
              :key="tag"
              :value="tag"
            />
          </datalist>
          <button
            data-test-id="dashboard_create_tinyurl_add_tag_btn"
            class="btn single-form__tags__add-tag"
            type="button"
            @click="addTag"
          >
            <i class="fa-solid fa-plus fa-lg text-blue" />
          </button>
        </div>
      </div>
      <ValidationMessage :message="urlForm.errors.get('tags')" />
      <template v-for="(tag, index) in urlForm.tags">
        <ValidationMessage
          v-if="`tags.${index}` in urlForm.errors.all()"
          :key="tag"
          class="mb-0"
          :message="urlForm.errors.all()[`tags.${index}`].join('<br>')"
        />
      </template>
    </div>

    <!-- Advanced Options -->
    <template v-if="hasAdvancedOptions">
      <TuInputLabel
        id="advanced-options"
        class="single-form__advanced-options"
        icon="fa-solid fa-sliders"
        label="Advanced Options"
      />

      <!-- UTM -->
      <div class="single-form__utm">
        <TuInputSwitch
          v-model="withUtm"
          :is-disabled="isFeatureLocked"
          heading="Add UTM"
        />
        <i
          v-tooltip="{
            content: `Use UTM tags to get more details about your traffic sources when using analytics tools outside of TinyURL`,
            trigger: isOnMobile ? 'click' : 'focus hover',
          }"
          class="fa fa-info-circle single-form__utm__tooltip"
          tabindex="0"
        />
        <a
          v-if="withUtm"
          href="javascript:void(0)"
          class="single-form__utm__edit-utm"
          @click.prevent="addUtm()"
        >
          <i class="fa-solid fa-pencil text-sm" /> Edit
        </a>
      </div>

      <!-- Link Expiry -->
      <div
        class="single-form__expiration"
        :class="{
          'single-form__expiration--visible': isCustomExpirationVisible,
        }"
      >
        <TuInputSwitch
          v-model="isCustomExpirationVisible"
          :is-disabled="isFeatureLocked"
          heading="Set Link Expiry"
        />
        <small class="single-form__expiration__helper-text">(Local Time)</small>
      </div>

      <div
        v-if="isCustomExpirationVisible"
        class="single-form__expiration"
      >
        <DatePicker
          v-model="expiresAt"
          class="w-100"
          type="datetime"
          format="MMM DD, YYYY hh:mm A"
          :placeholder="expirationDatePlaceholderText"
          :input-class="[
            'form-control single-form__expiration__date-picker',
            { 'is-invalid': urlForm.errors.has('expires_at') },
          ]"
          :disabled="isFormBusy"
          :disabled-date="disabledExpirationDateRange"
          :disabled-time="disabledExpirationTimeRange"
          @open="datepickerOpened = true"
          @close="datepickerOpened = false"
        >
          <template #icon-calendar>
            <i class="fa-solid fa-calendar text-blue" />
          </template>
        </DatePicker>
        <ValidationMessage :message="urlForm.errors.get('expires_at')" />
      </div>
    </template>

    <!-- Shorten a Link -->
    <TuButton
      label="Shorten Link"
      :is-block="true"
      :is-disabled="isFeatureLocked || isFormBusy"
      :severity="ButtonSeverity.SECONDARY"
      :is-loading="isFormBusy"
    />
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.single-form {
  $self: &;

  &__domain-and-alias {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: spacing.$xxs;

    &__hidden-button {
      position: absolute;
      pointer-events: none;
      opacity: 0;
    }

    &__separator {
      margin: 0 spacing.$xxxs;
      margin-top: spacing.$m;

      font-weight: typography.$font-weight-semi-bold;
    }

    &__domain-input,
    &__alias-input {
      flex: 1;
      min-width: 0%;
    }
  }

  &__expiration {
    display: flex;
    align-items: center;
    margin-bottom: spacing.$xxs;

    &__helper-text {
      margin-left: spacing.$xxxs;
      font-size: typography.$xxs-font-size;
      font-weight: typography.$font-weight-regular;
    }

    &--visible {
      margin-bottom: spacing.$xxxs;
    }
  }

  &__utm {
    display: flex;
    margin-bottom: spacing.$xxxs;

    &__tooltip {
      font-size: spacing.$xxxs;
      margin-left: spacing.$xs-5;
    }

    &__edit-utm {
      cursor: pointer;
      color: color.$primary;
      font-weight: typography.$font-weight-semi-bold;
      margin-left: spacing.$xxxs;
    }
  }

  &__tags {
    margin-bottom: spacing.$xxs;

    &__tag {
      background-color: color.$background-overlay;
      color: color.$text;

      display: flex;
      align-items: center;
    }

    &__tag-input {
      color: color.$text;
      margin-right: spacing.$xxxs;
      padding: spacing.$xxxxs spacing.$xxxs;
      width: 154px;

      &:focus {
        color: color.$text;
        border-color: color.$secondary;
        box-shadow: 0 0 0 0.2rem color.$secondary-light;
      }

      &::placeholder {
        color: color.$text-secondary;
        font-weight: typography.$font-weight-medium;
      }

      &::-webkit-calendar-picker-indicator {
        display: none !important;
      }
    }

    &__label {
      margin-bottom: spacing.$xxxs;
    }

    &__add-tag {
      font-weight: typography.$font-weight-medium;
      padding: 0;

      &:focus {
        color: color.$text;
        border-color: color.$secondary;
        box-shadow: 0 0 0 0.2rem color.$secondary-light;
      }
    }
  }

  &__advanced-options {
    margin-bottom: spacing.$xxs;
  }

  &--mobile {
    #{ $self }__domain-and-alias {
      display: block;
    }
  }
}
</style>

<style lang="scss">
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.single-form {
  $self: &;

  &__expiration {
    &__date-picker {
      padding-left: 30px;

      &:focus {
        color: color.$text;
        border-color: color.$secondary;
        box-shadow: 0 0 0 0.2rem color.$secondary-light;
      }

      &::placeholder {
        color: color.$text-secondary;
      }
    }
  }
}
</style>
